import React, { useEffect, useState, useMemo } from "react";
import { LuLock, LuSearch, LuX, LuShoppingCart, LuPackage, LuUser, LuArrowRight, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import useCachedProducts from "../hooks/UseCachedProducts";
import { getPrimaryImage } from "../utils/imageUtils";

// Maps any stored category value → the correct URL slug
const CATEGORY_SLUG = {
  "vapes":             "disposable-vapes",
  "disposable vapes":  "disposable-vapes",
  "disposable-vapes":  "disposable-vapes",
  "kratom":            "kratom",
  "novelty":           "novelty",
  "glass":             "glass",
  "delta":             "delta",
  "accessories":       "accessories",
};
const getCategorySlug = (cat) =>
  cat ? (CATEGORY_SLUG[cat.toLowerCase()] ?? cat.toLowerCase().replace(/\s+/g, "-")) : null;

// ─── component ────────────────────────────────────────────────────────────────

export default function ProductDetail() {
  const { productId }      = useParams();
  const { user: currentUser } = useAuth();
  const { addToCart }      = useCart();
  const navigate           = useNavigate();
  const location           = useLocation();
  const allProducts        = useCachedProducts();

  const [product, setProduct]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [quantity, setQuantity]           = useState(1);
  const [variantQuantities, setVariantQuantities] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantSearch, setVariantSearch] = useState("");

  // load product
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "products", productId));
        if (snap.exists() && !snap.data().hidden) {
          const data = { id: snap.id, ...snap.data() };
          setProduct(data);
          setActiveImageIndex(0);
          if (Array.isArray(data.variants) && data.variants.length) {
            const init = {};
            data.variants.forEach(v => { init[v] = 0; });
            setVariantQuantities(init);
          } else {
            setVariantQuantities({});
          }
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  // related products
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  }, [allProducts, product]);

  // zoom modal keyboard nav
  const showPrevImage = () => {
    if (!product?.imageUrls?.length) return;
    setSelectedImageIndex(i => (i === 0 ? product.imageUrls.length - 1 : i - 1));
  };
  const showNextImage = () => {
    if (!product?.imageUrls?.length) return;
    setSelectedImageIndex(i => (i === product.imageUrls.length - 1 ? 0 : i + 1));
  };
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape")      setSelectedImageIndex(null);
      if (e.key === "ArrowLeft")   showPrevImage();
      if (e.key === "ArrowRight")  showNextImage();
    };
    if (selectedImageIndex !== null) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [selectedImageIndex, product]);

  // variant helpers
  const hasVariants = Array.isArray(product?.variants) && product.variants.length > 0;
  const filteredVariants = useMemo(() => {
    if (!product?.variants) return [];
    const q = variantSearch.trim().toLowerCase();
    return q ? product.variants.filter(v => v.toLowerCase().includes(q)) : product.variants;
  }, [product?.variants, variantSearch]);
  const incVariant  = (v) => setVariantQuantities(p => ({ ...p, [v]: (p[v] || 0) + 1 }));
  const decVariant  = (v) => setVariantQuantities(p => ({ ...p, [v]: Math.max(0, (p[v] || 0) - 1) }));
  const setVariantQty = (v, val) => setVariantQuantities(p => ({ ...p, [v]: Math.max(0, val || 0) }));

  const handleAddNonVariant = () => {
    if (!currentUser) { toast.info("Please login to add items to cart."); navigate("/account", { state: { from: location } }); return; }
    if (product.quantity <= 0) { toast.error("This product is out of stock."); return; }
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart!`);
  };

  const handleAddVariant = () => {
    if (!currentUser) { toast.info("Please login to add items to cart."); navigate("/account", { state: { from: location } }); return; }
    if (product.quantity <= 0) { toast.error("This product is out of stock."); return; }
    const entries = Object.entries(variantQuantities).filter(([, qty]) => qty > 0);
    if (!entries.length) { toast.info("Please select a quantity for at least one variant."); return; }
    entries.forEach(([variantName, qty]) => addToCart({ ...product, selectedVariant: variantName }, qty));
    const total = entries.reduce((s, [, q]) => s + q, 0);
    toast.success(`Added ${total} unit${total > 1 ? "s" : ""} of ${product.name} to cart.`);
  };

  // ── guards ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="space-y-4 w-full max-w-5xl px-8 animate-pulse">
          <div className="h-6 bg-muted rounded w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 bg-muted rounded-2xl aspect-square" />
            <div className="lg:col-span-5 space-y-4">
              <div className="h-8 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-32 bg-muted rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-brand-navy font-bold text-xl">Product not found.</p>
        <Link to="/" className="text-brand-amber font-semibold hover:underline">← Back to Home</Link>
      </div>
    );
  }

  const mainImage = product.imageUrls?.[activeImageIndex] || getPrimaryImage(product);
  const inStock   = product.quantity > 0;

  // ── render ────────────────────────────────────────────────────────────────

  const productImage = product ? getPrimaryImage(product) : "";
  const productJsonLd = product ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `Wholesale ${product.name} available at Khoja Distributions, DFW.`,
    "image": productImage,
    "sku": product.id,
    "category": product.category,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": product.price,
      "availability": product.quantity > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "seller": { "@type": "Organization", "name": "Khoja Distributions" }
    }
  }) : null;

  return (
    <div className="min-h-screen bg-background font-sans">
      {product && (
        <Helmet>
          <title>{product.name} | Wholesale — Khoja Distributions</title>
          <meta name="description" content={product.description ? product.description.slice(0, 155) : `Buy wholesale ${product.name} from Khoja Distributions. Bulk pricing for DFW retailers.`} />
          <link rel="canonical" href={`https://www.khojadistributions.com/products/details/${product.id}`} />
          <meta property="og:title" content={`${product.name} | Khoja Distributions`} />
          <meta property="og:description" content={product.description || `Wholesale ${product.name} — bulk pricing for DFW retailers.`} />
          <meta property="og:url" content={`https://www.khojadistributions.com/products/details/${product.id}`} />
          {productImage && <meta property="og:image" content={productImage} />}
          <script type="application/ld+json">{productJsonLd}</script>
        </Helmet>
      )}

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-2">
        <nav className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-medium">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <span>/</span>
          {product.category && (
            <>
              <Link
                to={`/products/${getCategorySlug(product.category)}`}
                className="hover:text-foreground transition-colors capitalize"
              >
                {product.category}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="text-foreground font-semibold truncate max-w-[200px]">{product.name}</span>
        </nav>
      </div>

      {/* ── Hero: gallery + info ──────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-12">

        {/* Gallery — 7 cols */}
        <motion.div
          className="lg:col-span-7 flex flex-col md:flex-row gap-4 lg:sticky lg:top-10 lg:self-start"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Vertical thumbnails — max 5 shown, +N badge for extras */}
          {product.imageUrls?.length > 1 && (
            <div className="flex md:flex-col gap-3 order-2 md:order-1 overflow-x-auto md:overflow-visible flex-shrink-0">
              {product.imageUrls.slice(0, 5).map((url, idx) => {
                const isLast = idx === 4 && product.imageUrls.length > 5;
                return (
                  <button
                    key={idx}
                    onClick={() => { setActiveImageIndex(idx); if (isLast) setSelectedImageIndex(idx); }}
                    className={`relative flex-shrink-0 w-20 h-20 rounded-xl border-2 flex items-center justify-center p-2 transition-all ${
                      idx === activeImageIndex
                        ? "border-brand-amber bg-[#d3e4fc] dark:bg-[#1A1E2F]"
                        : "border-transparent bg-[#d3e4fc] dark:bg-[#1A1E2F] hover:border-brand-navy/30 dark:hover:border-accent/30"
                    }`}
                  >
                    <img src={url} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-contain" />
                    {isLast && (
                      <div className="absolute inset-0 rounded-xl bg-brand-navy/60 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">+{product.imageUrls.length - 5}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Main image */}
          <div
            className="flex-grow order-1 md:order-2 bg-card rounded-2xl overflow-hidden flex items-center justify-center p-10 min-h-[420px] cursor-zoom-in border border-border shadow-sm group"
            onClick={() => product.imageUrls?.length && setSelectedImageIndex(activeImageIndex)}
          >
            <img
              src={mainImage}
              alt={product.name}
              className="max-h-[420px] w-full object-contain transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        </motion.div>

        {/* Info panel — 5 cols */}
        <motion.div
          className="lg:col-span-5 flex flex-col justify-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {/* Category badge */}
          {product.category && (
            <span className="inline-block bg-muted text-[var(--color-muted-foreground)] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4 w-fit">
              {product.category}
            </span>
          )}

          {/* Name */}
          <h1 className="text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight mb-2 leading-tight">
            {product.name}
          </h1>

          {/* Stock status */}
          <div className="flex items-center gap-2 mb-6">
            <span className={`w-2 h-2 rounded-full ${inStock ? "bg-green-500" : "bg-red-400"}`} />
            <span className={`text-sm font-semibold ${inStock ? "text-green-600" : "text-red-500"}`}>
              {inStock ? "In Stock" : "Out of Stock"}
            </span>
          </div>

          {/* Pricing gate or price */}
          {currentUser ? (
            <div className="mb-6">
              <p className="text-3xl font-black text-foreground mb-1">${product.price?.toFixed(2)}</p>
              <p className="text-sm text-[var(--color-muted-foreground)]">Per unit · Bulk pricing available</p>
            </div>
          ) : (
            <div className="bg-[#e4efff] dark:bg-[#1A1E2F] rounded-2xl p-6 mb-6 border-l-4 border-brand-amber">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-xl bg-brand-amber/10 flex items-center justify-center flex-shrink-0">
                  <LuLock size={20} className="text-brand-amber" />
                </div>
                <div>
                  <p className="font-bold text-foreground">Wholesale Pricing Protected</p>
                  <p className="text-sm text-[var(--color-muted-foreground)] leading-relaxed mt-1">
                    Login to your verified distributor account to view bulk pricing and inventory availability.
                  </p>
                </div>
              </div>
              <Link
                to="/account"
                state={{ from: location }}
                className="block w-full bg-brand-amber text-white font-bold py-3.5 rounded-xl text-center hover:brightness-110 transition-all"
              >
                Login to View Pricing
              </Link>
              <p className="text-center mt-3 text-xs text-slate-400">
                Don't have an account?{" "}
                <Link to="/register" className="underline text-brand-navy font-semibold hover:text-brand-amber">
                  Apply for Wholesale Access
                </Link>
              </p>
            </div>
          )}

          {/* Variant pills */}
          {hasVariants && (
            <div className="mb-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-brand-navy uppercase tracking-wider">Select Flavor</p>
                <span className="text-xs font-bold text-brand-amber">{product.variants.length} Variants Available</span>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <input
                  type="text"
                  placeholder="Search flavor..."
                  value={variantSearch}
                  onChange={e => setVariantSearch(e.target.value)}
                  className="w-full bg-card border border-border text-foreground placeholder:text-[var(--color-muted-foreground)] rounded-full px-10 py-2.5 text-sm focus:ring-2 focus:ring-ring focus:border-transparent outline-none transition-all"
                />
                <LuSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                {variantSearch && (
                  <button onClick={() => setVariantSearch("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-navy">
                    <LuX size={16} />
                  </button>
                )}
              </div>

              {/* Pills */}
              <div className="flex flex-wrap gap-2">
                {filteredVariants.length > 0 ? filteredVariants.map(variant => {
                  const qty = variantQuantities[variant] || 0;
                  const isSelected = selectedVariant === variant;
                  return (
                    <button
                      key={variant}
                      onClick={() => setSelectedVariant(v => v === variant ? null : variant)}
                      className={`relative px-3.5 py-1.5 rounded-full border-2 text-[11px] font-bold uppercase tracking-wide transition-all ${
                        isSelected
                          ? "border-brand-navy dark:border-accent bg-brand-navy dark:bg-accent text-white dark:text-[#0F172A]"
                          : qty > 0
                          ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                          : "border-border hover:border-brand-navy dark:hover:border-accent text-foreground bg-card"
                      }`}
                    >
                      {variant}
                      {qty > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-brand-amber rounded-full text-white text-[9px] flex items-center justify-center font-black leading-none">
                          {qty}
                        </span>
                      )}
                    </button>
                  );
                }) : (
                  <p className="text-sm text-slate-400 italic">No flavors match "{variantSearch}"</p>
                )}
              </div>

              {/* Qty controls for selected variant */}
              {selectedVariant && (
                <div className="mt-4 flex items-center justify-between p-3 bg-muted rounded-xl border border-border">
                  <span className="text-sm font-bold text-foreground truncate mr-3">{selectedVariant}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => decVariant(selectedVariant)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-card text-foreground font-bold transition-colors">−</button>
                    <input
                      type="number" min="0"
                      value={variantQuantities[selectedVariant] || 0}
                      onChange={e => setVariantQty(selectedVariant, Number(e.target.value))}
                      className="w-12 border border-border rounded-lg text-center text-sm font-bold py-1.5 focus:outline-none focus:border-ring bg-card text-foreground"
                    />
                    <button onClick={() => incVariant(selectedVariant)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-border hover:bg-card text-foreground font-bold transition-colors">+</button>
                  </div>
                </div>
              )}

              {/* Add to cart */}
              {(() => {
                const totalUnits = Object.values(variantQuantities).reduce((s, q) => s + q, 0);
                return (
                  <button
                    onClick={handleAddVariant}
                    disabled={!inStock}
                    className="mt-4 w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-40 transition-colors text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2"
                  >
                    <LuShoppingCart size={20} />
                    {totalUnits > 0 ? `Add to Cart · ${totalUnits} unit${totalUnits > 1 ? "s" : ""}` : "Add to Cart"}
                  </button>
                );
              })()}
            </div>
          )}

          {/* Non-variant qty + add */}
          {!hasVariants && (
            <div className="mb-6">
              <p className="text-sm font-bold text-brand-navy mb-3 uppercase tracking-wider">Quantity</p>
              <div className="flex items-center gap-3 mb-4">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-10 rounded-xl border border-border flex items-center justify-center text-foreground font-bold hover:bg-muted transition-colors">−</button>
                <input
                  type="number" min="1" max={product.quantity} value={quantity}
                  onChange={e => setQuantity(Math.max(1, Math.min(product.quantity, Number(e.target.value) || 1)))}
                  className="w-16 border border-border rounded-xl text-center font-bold text-foreground bg-card py-2 focus:outline-none focus:border-ring"
                />
                <button onClick={() => setQuantity(q => Math.min(product.quantity, q + 1))} className="w-10 h-10 rounded-xl border border-border flex items-center justify-center text-foreground font-bold hover:bg-muted transition-colors">+</button>
              </div>
              <button
                onClick={handleAddNonVariant}
                disabled={!inStock}
                className="w-full bg-brand-navy hover:bg-brand-amber disabled:opacity-40 transition-colors text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2"
              >
                <LuShoppingCart size={20} />
                Add to Cart
              </button>
            </div>
          )}

          {/* Quick specs */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted-foreground)] mb-1">Category</p>
              <p className="font-bold text-foreground capitalize">{product.category || "—"}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted-foreground)] mb-1">Availability</p>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${inStock ? "bg-green-500" : "bg-red-400"}`} />
                <p className="font-bold text-foreground">{inStock ? "In Stock" : "Out of Stock"}</p>
              </div>
            </div>
            {product.variants?.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted-foreground)] mb-1">Variants</p>
                <p className="font-bold text-foreground">{product.variants.length} options</p>
              </div>
            )}
            {currentUser && product.price && (
              <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted-foreground)] mb-1">Unit Price</p>
                <p className="font-bold text-foreground">${product.price.toFixed(2)}</p>
              </div>
            )}
          </div>

          {/* Shipping note */}
          <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
            <LuPackage size={16} className="text-brand-amber flex-shrink-0" />
            Free delivery on all orders over <strong className="text-brand-navy ml-1">$500</strong>
          </div>
        </motion.div>
      </section>

      {/* ── Description + Bulk Matrix ─────────────────────────────────────── */}
      <section className="bg-[#eef4ff] dark:bg-[#0E1223] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">

          {/* Description */}
          <div>
            <h2 className="text-2xl font-black text-foreground mb-8 tracking-tight">Product Details</h2>
            {product.description ? (
              <div className="space-y-4 text-[var(--color-muted-foreground)] leading-relaxed">
                {product.description.split("\n").filter(Boolean).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 italic">No description available for this product.</p>
            )}

            {/* Key detail rows */}
            <div className="mt-10 space-y-4 border-t border-border pt-8">
              {[
                ["Category",     product.category],
                ["Variants",     product.variants?.length ? `${product.variants.length} available` : "Single item"],
                ["Availability", inStock ? "In Stock" : "Out of Stock"],
                ["Wholesale",    "B2B accounts only · Login required for pricing"],
              ].map(([label, value]) => value && (
                <div key={label} className="flex justify-between border-b border-border/60 pb-4">
                  <span className="text-[var(--color-muted-foreground)] font-medium">{label}</span>
                  <span className="font-bold text-foreground text-right max-w-[60%]">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bulk Tier Matrix */}
          <div className="rounded-3xl bg-[#d3e4fc] dark:bg-[#1A1E2F] p-8">
            <h2 className="text-2xl font-black text-foreground mb-6 tracking-tight">Bulk Tier Matrix</h2>

            {currentUser ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 bg-brand-navy text-white px-4 py-3 rounded-xl font-bold text-xs">
                  <div>QUANTITY</div>
                  <div className="text-center">PRICE / UNIT</div>
                  <div className="text-center">AVAILABILITY</div>
                </div>
                {[
                  ["10 – 50 units",     0.95, "high"],
                  ["51 – 250 units",    0.85, "high"],
                  ["251 – 1000 units",  0.75, "limited"],
                  ["1000+ units",       null, "contact"],
                ].map(([range, mult, avail]) => (
                  <div key={range} className="grid grid-cols-3 items-center bg-card px-4 py-3 rounded-xl text-sm border border-border">
                    <div className="font-bold text-foreground">{range}</div>
                    <div className="text-center font-bold text-brand-amber">
                      {mult && product.price ? `$${(product.price * mult).toFixed(2)}` : "Contact Us"}
                    </div>
                    <div className={`text-center text-xs font-bold ${avail === "high" ? "text-green-600" : avail === "limited" ? "text-orange-500" : "text-slate-400"}`}>
                      {avail === "high" ? "● High" : avail === "limited" ? "● Limited" : "Call Us"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="bg-card p-7 rounded-2xl shadow-2xl max-w-xs text-center border border-border">
                  <div className="w-12 h-12 rounded-2xl bg-brand-amber/10 flex items-center justify-center mx-auto mb-4">
                    <LuUser size={24} className="text-brand-amber" />
                  </div>
                  <p className="font-bold text-foreground mb-2">Authenticated View Only</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mb-5 leading-relaxed">
                    Log in to see real-time inventory and your custom contract pricing.
                  </p>
                  <Link
                    to="/account"
                    state={{ from: location }}
                    className="block w-full bg-brand-navy text-white text-sm font-bold px-6 py-2.5 rounded-xl hover:bg-brand-amber transition-colors"
                  >
                    Login Now
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Related Products ──────────────────────────────────────────────── */}
      {relatedProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="text-3xl font-black text-foreground tracking-tight">Frequently Complemented</h2>
              <p className="text-[var(--color-muted-foreground)] mt-1 text-sm">Add these to your next wholesale order.</p>
            </div>
            {product.category && (
              <Link
                to={`/products/${getCategorySlug(product.category)}`}
                className="text-brand-navy font-bold flex items-center gap-2 hover:text-brand-amber transition-colors text-sm"
              >
                View Catalog
                <LuArrowRight size={16} />
              </Link>
            )}
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {relatedProducts.map(prod => (
              <motion.div
                key={prod.id}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              >
                <Link to={`/products/details/${prod.id}`} className="group block">
                  <div className="bg-[#d3e4fc] dark:bg-muted hover:bg-white dark:hover:bg-card rounded-2xl p-6 aspect-square flex items-center justify-center transition-all duration-300 group-hover:shadow-lg mb-4">
                    <img
                      src={getPrimaryImage(prod)}
                      alt={prod.name}
                      className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  {prod.category && (
                    <p className="text-[10px] font-bold tracking-widest uppercase text-brand-amber mb-1">{prod.category}</p>
                  )}
                  <h3 className="font-bold text-foreground text-base group-hover:text-brand-amber transition-colors leading-snug line-clamp-2">
                    {prod.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-bold text-[var(--color-muted-foreground)] bg-muted px-2 py-0.5 rounded">Bulk Only</span>
                    <span className="text-xs font-bold text-green-600">In Stock</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* ── Zoom Modal ────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {selectedImageIndex !== null && product.imageUrls?.[selectedImageIndex] && (
          <motion.div
            className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center"
            onClick={() => setSelectedImageIndex(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative max-w-3xl w-full max-h-[90vh] flex items-center justify-center px-12"
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
            >
              <button
                onClick={showPrevImage}
                className="absolute left-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <LuChevronLeft size={20} />
              </button>
              <img
                src={product.imageUrls[selectedImageIndex]}
                alt="Zoomed product"
                className="w-full h-auto object-contain rounded-2xl shadow-2xl"
              />
              <button
                onClick={showNextImage}
                className="absolute right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <LuChevronRight size={20} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
